import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { MOVIES, REGIONS_FOR, filterCatalog, getPopularSeries, searchCatalog } from '@/data/catalog';
import type { Movie, SortKey } from '@/types/movie';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterBar, { type FilterValue } from '@/components/feature/FilterBar';
import Pagination from '@/components/feature/Pagination';

const PAGE_SIZE = 20;
const DEFAULT_FILTERS: FilterValue = { region: 'all', year: 'all', sort: 'default' };
const HOT_KEYWORDS = ['星海', '悬疑', '科幻', '国产剧', '治愈', '2026', '犯罪', '都市'];

export default function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const keyword = (searchParams.get('q') || '').trim();

  const [input, setInput] = useState(keyword);
  const [filters, setFilters] = useState<FilterValue>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInput(keyword);
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  }, [keyword]);

  useDocumentMeta({
    title: keyword ? `「${keyword}」搜索结果 - 大雷影视` : '搜索影视 - 大雷影视大全',
    description: keyword
      ? `大雷影视为你找到与「${keyword}」相关的影视作品，包含电影、电视剧的年份、地区与类型信息。`
      : '在大雷影视搜索电影、电视剧，支持按片名、导演、主演与类型模糊匹配。',
    keywords: '大雷影视,影视搜索,电影搜索,电视剧搜索,影视大全',
  });

  const results: Movie[] = useMemo(() => (keyword ? searchCatalog(keyword) : []), [keyword]);
  const filtered = useMemo(() => filterCatalog(results, filters), [results, filters]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const hotSuggestions = useMemo(() => getPopularSeries(10), []);

  const runSearch = (value: string) => {
    const trimmed = value.trim();
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : '/search');
  };

  const handleFilterChange = (next: FilterValue) => {
    setFilters(next);
    setPage(1);
  };

  return (
    <div>
      <div className="w-full bg-primary-950 py-9 md:py-12">
        <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8">
          <Breadcrumb items={[{ label: '搜索' }]} light />
          <h1 className="mt-5 text-2xl font-bold text-background-50 md:text-3xl">搜索影视作品</h1>
          <p className="mt-2 text-sm text-background-300/80">
            支持片名、外文名、导演、主演与类型的模糊匹配，站内共 {MOVIES.length} 部演示作品
          </p>

          <form
            className="mt-6 flex w-full max-w-3xl flex-col gap-2 sm:flex-row"
            onSubmit={(event) => {
              event.preventDefault();
              runSearch(input);
            }}
            role="search"
          >
            <div className="flex h-12 flex-1 items-center gap-2.5 rounded-md bg-background-50 px-4">
              <i className="ri-search-line text-lg text-foreground-400" />
              <input
                type="search"
                name="q"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入影视名称、演员或类型，例如：悬疑"
                aria-label="搜索关键词"
                className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
              />
              {input && (
                <button
                  type="button"
                  onClick={() => setInput('')}
                  aria-label="清空关键词"
                  className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-foreground-400 hover:bg-background-100"
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="h-12 shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-7 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-background-400">热门搜索</span>
            {HOT_KEYWORDS.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => runSearch(word)}
                className="cursor-pointer rounded-full border border-background-50/20 px-2.5 py-1 text-xs text-background-200/80 transition-colors hover:border-accent-500 hover:text-accent-300"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
        {keyword ? (
          <>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <h2 className="text-lg font-medium text-foreground-950">
                  搜索结果：<span className="text-primary-600">「{keyword}」</span>
                </h2>
                <p className="mt-1.5 text-sm text-foreground-500">
                  共找到 <span className="font-label text-base font-semibold text-accent-600">{filtered.length}</span> 个结果
                  {filtered.length !== results.length && `（关键词命中 ${results.length} 部，已按筛选条件过滤）`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="cursor-pointer text-sm text-foreground-400 transition-colors hover:text-primary-600"
              >
                返回首页
              </button>
            </div>

            {results.length > 0 && (
              <div className="mt-5">
                <FilterBar
                  value={filters}
                  onChange={handleFilterChange}
                  regions={REGIONS_FOR(results)}
                  onReset={() => {
                    setFilters(DEFAULT_FILTERS);
                    setPage(1);
                  }}
                />
              </div>
            )}

            <div className="mt-7">
              {pageItems.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 md:grid-cols-4 md:gap-x-4 lg:grid-cols-5">
                    {pageItems.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                  <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
                </>
              ) : (
                <div>
                  <EmptyState
                    title="未找到相关影视作品"
                    description={`没有匹配「${keyword}」的内容。可以尝试更换关键词，或从下面的热门影视里挑一部看看。`}
                    actionLabel="浏览热门影视"
                    actionPath="/category/hot"
                  />
                  <div className="mt-12">
                    <div className="mb-5 flex items-center gap-2.5">
                      <span className="h-5 w-1 rounded-full bg-accent-500" />
                      <h3 className="text-lg font-bold text-foreground-950">热门影视推荐</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 md:grid-cols-4 md:gap-x-4 lg:grid-cols-5">
                      {hotSuggestions.map((movie) => (
                        <MovieCard key={movie.id} movie={movie} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <EmptyState
              title="输入关键词开始搜索"
              description="可以搜索片名、导演、主演或者类型，例如「悬疑」「星海」「治愈」。也支持下方的热门关键词。"
            />
            <div className="mt-12">
              <div className="mb-5 flex items-center gap-2.5">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                <h2 className="text-lg font-bold text-foreground-950">近期热门剧集</h2>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 md:grid-cols-4 md:gap-x-4 lg:grid-cols-5">
                {hotSuggestions.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}